import { DashboardPageFormBody, DashboardPageFormShell } from '@shigo-ui/dashboard-ui';
import { Button, Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import {
  dealFormValueToPayload,
  dealToFormValue,
  emptyDealFormValue,
  type DealFormValue,
} from '../../model';
import { useDealMutations, useDealQuery } from '../../hooks';
import { DealEditorForm } from './DealEditorForm';

export function DealEditorPage(props: { dealId?: string }) {
  const { returnTo } = useReturnNavigation({ to: '/demand/deals' });
  const editing = Boolean(props.dealId);
  const query = useDealQuery(props.dealId);
  const mutations = useDealMutations();
  const saving = mutations.create.isPending || mutations.update.isPending;
  const waitingForFreshDetail = editing && !query.isFetchedAfterMount;
  const initialValue = query.data ? dealToFormValue(query.data) : emptyDealFormValue;

  async function save(value: DealFormValue) {
    try {
      const payload = dealFormValueToPayload(value, query.data);
      if (props.dealId) {
        await mutations.update.mutateAsync({ id: props.dealId, payload });
      } else {
        await mutations.create.mutateAsync(payload);
      }
      toast.success(editing ? 'Deal updated.' : 'Deal created.');
      await returnTo({ replace: true });
    } catch (error) {
      showRequestError(error, 'Deal could not be saved.');
    }
  }

  let content;
  if (waitingForFreshDetail && !query.isError) {
    content = (
      <div role="status" aria-label="Loading deal" className="grid gap-4 p-6">
        <Skeleton className="h-10 w-72 max-w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  } else if (editing && query.isError) {
    content = (
      <DashboardPageFormBody title="Deal could not be loaded.">
        <Button type="button" variant="outline" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </DashboardPageFormBody>
    );
  } else {
    content = (
      <DealEditorForm
        key={props.dealId ?? 'create'}
        initialValue={initialValue}
        saving={saving}
        onCancel={() => void returnTo()}
        onSubmit={save}
      />
    );
  }

  return (
    <DashboardPageFormShell
      title={dashboardT(editing ? 'deals.editTitle' : 'deals.createTitle')}
      backAction={<ReturnBackLink fallback={{ to: '/demand/deals' }} label="Back to deals" />}
    >
      {content}
    </DashboardPageFormShell>
  );
}
